import React, { useCallback, useEffect, useMemo, useState } from "react";
import OutletSearch from "../../SelectSearch/OutletSearch";
import axios from "axios";
import PageStatus from "../../PageStatus/PageStatus";
import SalesVATTable from "./SalesVATTable";
import PurchaseVATTable from "./PurchaseVATTable";
import { adToBs, bsToAd } from "@sbmdkl/nepali-date-converter";
import { NP_MONTHS } from './../../../data/months';
import { formatSalesValue } from "../../../utils/formatUtils";

const pad2 = (n) => String(n).padStart(2, "0");

const toISODate = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const parseDateStr = (str) => {
    const [year, month, day] = str.split("-").map(Number);
    return { year, month, day };
};

const getNepaliMonthRangeAD = (bsYear, bsMonth) => {
    const startAD = bsToAd(`${bsYear}-${pad2(bsMonth)}-01`);

    let nextMonth = bsMonth + 1;
    let nextYear = bsYear;
    if (nextMonth > 12) {
        nextMonth = 1;
        nextYear += 1;
    }

    const firstDayNextAD = bsToAd(`${nextYear}-${pad2(nextMonth)}-01`);
    const endADDate = new Date(firstDayNextAD);
    endADDate.setDate(endADDate.getDate() - 1);
    const endAD = toISODate(endADDate);

    return { startAD, endAD };
};

// A single row inside the VAT summary sidebar
const SummaryRow = ({ label, value, tone = "default" }) => {
    const toneClasses =
        tone === "positive"
            ? "text-green-600"
            : tone === "negative"
                ? "text-red-600"
                : "text-gray-900";

    return (
        <div className="flex items-center justify-between gap-3 py-2">
            <span
                className="text-xs font-medium uppercase tracking-wide text-gray-700"
            >
                {label}
            </span>
            <span
                className={`text-sm font-semibold ${toneClasses}`}
            >
                {value}
            </span>
        </div>
    );
};

const MonthlyVATPayment = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const [selectedOutlet, setSelectedOutlet] = useState(null);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [npMonthLabel, setNpMonthLabel] = useState("");

    const [salesData, setSalesData] = useState([]);
    const [purchaseData, setPurchaseData] = useState([]);

    // Static for now — wire this up to a real "previous balance" source
    // whenever that becomes available.
    const [previousBalance] = useState(0);

    const [loading, setLoading] = useState({
        sales: false,
        purchase: false,
    });

    const [error, setError] = useState({
        sales: null,
        purchase: null,
    });

    useEffect(() => {
        try {
            const todayStr = ("2026-07-15");
            const bsTodayStr = adToBs(todayStr);
            const { year, month } = parseDateStr(bsTodayStr);

            const { startAD, endAD } = getNepaliMonthRangeAD(year, month);

            setStartDate(startAD);
            setEndDate(endAD);
            setNpMonthLabel(`${NP_MONTHS[month - 1]} ${year}`);
        } catch (err) {
            console.error("Error computing Nepali month range:", err);
        }
    }, []);

    const fetchSalesHistory = useCallback(async () => {
        if (!selectedOutlet || !token || !startDate || !endDate) return;

        setLoading((prev) => ({ ...prev, sales: true }));
        setError((prev) => ({ ...prev, sales: null }));

        try {
            const res = await axios.post(`${baseUrl}/saleshistory`, {
                outlet: selectedOutlet,
                dateStart: startDate,
                dateEnd: endDate,
                token,
            });

            setSalesData(res.data.orderDetails || []);
        } catch (err) {
            console.error("Error fetching sales history:", err);

            setError((prev) => ({
                ...prev,
                sales:
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    "Failed to fetch sales history.",
            }));
        } finally {
            setLoading((prev) => ({ ...prev, sales: false }));
        }
    }, [baseUrl, token, selectedOutlet, startDate, endDate]);

    const fetchPurchaseHistory = useCallback(async () => {
        if (!selectedOutlet || !startDate || !endDate) return;

        setLoading((prev) => ({ ...prev, purchase: true }));
        setError((prev) => ({ ...prev, purchase: null }));

        try {
            const res = await axios.get(
                `${baseUrl}/reqfilterfirst/?secondtime=${endDate}&firsttime=${startDate}&outlet_name=${encodeURIComponent(
                    selectedOutlet
                )}`
            );

            setPurchaseData(res.data.purchaserequisition || []);
        } catch (err) {
            console.error("Error fetching purchase requisitions:", err);

            setError((prev) => ({
                ...prev,
                purchase:
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    "Failed to fetch purchase history.",
            }));
        } finally {
            setLoading((prev) => ({ ...prev, purchase: false }));
        }
    }, [baseUrl, selectedOutlet, startDate, endDate]);

    useEffect(() => {
        fetchSalesHistory();
        fetchPurchaseHistory();
    }, [fetchSalesHistory, fetchPurchaseHistory]);

    // Same "has a real bill number" filter used inside SalesVATTable, kept
    // in sync here so the sidebar totals match what the table displays.
    const validSales = useMemo(
        () => (salesData || []).filter((item) => item.bill_no?.trim()),
        [salesData]
    );

    const totalVatPayable = useMemo(
        () => validSales.reduce((sum, item) => sum + Number(item.VAT || 0), 0),
        [validSales]
    );

    const totalVatReceivable = useMemo(
        () =>
            (purchaseData || []).reduce(
                (sum, item) => sum + Number(item.TaxAmount || 0),
                0
            ),
        [purchaseData]
    );

    const vatForThisMonth = totalVatPayable - totalVatReceivable;

    return (
        <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
                <div className="flex justify-center mb-2.5">
                    <OutletSearch
                        selectedOutlet={selectedOutlet}
                        setSelectedOutlet={setSelectedOutlet}
                        token={token}
                        compact
                    />
                </div>

                {npMonthLabel && (
                    <div className="mb-1 text-center text-sm text-gray-500">
                        Showing data for <span className="font-semibold">{npMonthLabel}</span>{" "}
                        ({startDate} to {endDate})
                    </div>
                )}

                <PageStatus
                    loading={loading.sales}
                    error={error.sales}
                    loadingText="Loading sales data..."
                />

                {!loading.sales && !error.sales && (
                    <div className="mb-6">
                        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-600">
                            Sales
                        </h2>
                        <SalesVATTable order={salesData} />
                    </div>
                )}

                <PageStatus
                    loading={loading.purchase}
                    error={error.purchase}
                    loadingText="Loading purchase requisition data..."
                />

                {!loading.purchase && !error.purchase && (
                    <div className="mb-6">
                        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-600">
                            Purchase
                        </h2>
                        <PurchaseVATTable purchaseRequisitions={purchaseData} />
                    </div>
                )}
            </div>

            <aside className="sticky top-4 h-fit w-72 shrink-0 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-600">
                    VAT Summary
                </h2>

                <div className="divide-y divide-gray-100">
                    <SummaryRow
                        label="Total VAT Payable"
                        value={formatSalesValue(totalVatPayable)}
                    />

                    <SummaryRow
                        label="Total VAT Receivable"
                        value={formatSalesValue(totalVatReceivable)}
                    />

                    <SummaryRow
                        label="Previous Balance"
                        value={formatSalesValue(previousBalance)}
                    />

                    <SummaryRow
                        label="VAT For This Month"
                        value={formatSalesValue(vatForThisMonth)}
                        tone={vatForThisMonth >= 0 ? "negative" : "positive"}
                    />
                </div>
            </aside>
        </div>
    );
};

export default MonthlyVATPayment;